<template>
  <article
    class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-teal-200 transition-all duration-300 flex flex-col justify-between cursor-pointer relative overflow-hidden group"
    @click="onClick"
  >
    <!-- Card Top Header -->
    <div>
      <div class="flex items-start justify-between gap-3 mb-4">
        <!-- Sponsor Logo Frame -->
        <div
          class="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 p-1 flex-shrink-0 flex items-center justify-center overflow-hidden"
        >
          <img
            v-if="match.sponsorLogoUrl"
            :src="match.sponsorLogoUrl"
            :alt="match.sponsorName"
            class="w-full h-full object-contain"
          />
          <div
            v-else
            class="w-full h-full bg-slate-800 text-white flex items-center justify-center font-bold text-base rounded-lg"
          >
            {{ (match.sponsorName || '?')[0].toUpperCase() }}
          </div>
        </div>

        <!-- Score Gauge & Booth info -->
        <div class="flex flex-col items-end text-right">
          <div class="flex items-baseline gap-0.5 text-teal-600">
            <span class="text-xl font-extrabold tracking-tight">
              {{ displayScore }}
            </span>
            <span class="text-xxs font-bold uppercase tracking-wider">%</span>
          </div>
          <span
            class="text-xxs uppercase tracking-wider font-semibold text-slate-400"
            >Match Score</span
          >
        </div>
      </div>

      <!-- Sponsor Name & Booth -->
      <div class="mb-3">
        <h3
          class="font-extrabold text-base text-slate-900 group-hover:text-teal-600 transition-colors truncate"
        >
          {{ match.sponsorName }}
        </h3>
        <div class="flex flex-wrap items-center gap-2 mt-1">
          <span
            v-if="match.sponsorBoothLabel"
            class="text-xs text-slate-400 flex items-center gap-1"
          >
            <i class="fal fa-map-marker-alt fa-fw text-slate-400"></i>
            Booth {{ match.sponsorBoothLabel }}
          </span>
          <span
            v-if="match.sponsorIndustryLabel"
            class="text-xxs font-bold uppercase tracking-widest text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100"
          >
            {{ match.sponsorIndustryLabel }}
          </span>
        </div>
      </div>

      <!-- Description -->
      <p
        v-if="descriptionText"
        class="text-sm text-slate-500 line-clamp-2 mb-4 leading-relaxed"
      >
        {{ descriptionText }}
      </p>

      <!-- Why This Match Alert Container -->
      <div
        v-if="match.explanationShort"
        class="mb-5 p-3.5 bg-teal-50/50 rounded-xl border border-teal-100/50 flex gap-2.5"
      >
        <i
          class="fas fa-sparkles fa-fw text-teal-500 text-xs mt-0.5 shrink-0 animate-pulse"
        ></i>
        <div class="text-xxs text-slate-600 font-medium leading-relaxed">
          <span
            class="text-teal-700 font-bold uppercase tracking-wider block mb-0.5"
            >Why This Match?</span
          >
          {{ match.explanationShort }}
        </div>
      </div>
    </div>

    <!-- Quick Action Footer Utilities -->
    <div
      class="border-t border-slate-100 pt-4 flex items-center justify-between gap-2 mt-auto"
    >
      <button
        @click.stop="onClick"
        class="flex-1 py-2 px-3 text-xs font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all text-center min-h-[44px] sm:min-h-[40px] flex items-center justify-center"
      >
        View Profile
      </button>

      <div class="flex items-center gap-1.5 shrink-0">
        <button
          @click.stop="$emit('book', match)"
          class="action-btn rounded-xl bg-teal-50 text-teal-600 hover:bg-teal-100 flex items-center justify-center transition-colors"
          title="Book Meeting"
        >
          <i class="fal fa-calendar-plus fa-fw text-base"></i>
        </button>
      </div>
    </div>
  </article>
</template>

<script>
import { stripHtml } from '@/helpers/sanitize';
import { displayScore } from '@/helpers/match-score';

export default {
  name: 'MatchCard',
  props: {
    match: { type: Object, required: true }
  },
  computed: {
    displayScore() {
      // Stored 0-100 — never rescale. See helpers/match-score.
      return displayScore(this.match.calibratedScore);
    },
    // Sponsor description is sponsor-authored rich text; the card shows a 2-line excerpt, so
    // strip tags to plain text (the detail view renders the full sanitized HTML).
    descriptionText() {
      return stripHtml(this.match.sponsorDescription);
    }
  },
  methods: {
    onClick() {
      this.$emit('open', this.match);
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.text-xxs {
  font-size: 0.65rem;
}

/* Strictly Limit Icon & SVG Sizes to Prevent Giant Expand Bug */
i.fal,
i.fas,
i.far,
i.fab,
svg {
  width: 1.125rem !important;
  height: 1.125rem !important;
  min-width: 1.125rem !important;
  min-height: 1.125rem !important;
  max-width: 1.125rem !important;
  max-height: 1.125rem !important;
  font-size: 1.125rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Micro sizing for secondary descriptors (booth pins and sparkles) */
.text-xs i,
.text-xs svg,
.text-xxs i,
.text-xxs svg,
.fas.fa-sparkles,
svg.fa-sparkles {
  width: 0.75rem !important;
  height: 0.75rem !important;
  min-width: 0.75rem !important;
  min-height: 0.75rem !important;
  max-width: 0.75rem !important;
  max-height: 0.75rem !important;
  font-size: 0.75rem !important;
}

/* Guarantee size limits on action buttons.
   Mobile-first: 44px minimum tap target; desktop (sm+) restores the original 40px chip. */
button.action-btn {
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  max-width: 2.75rem !important;
  max-height: 2.75rem !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}
@media (min-width: 640px) {
  button.action-btn {
    width: 2.5rem !important;
    height: 2.5rem !important;
    min-width: 2.5rem !important;
    min-height: 2.5rem !important;
    max-width: 2.5rem !important;
    max-height: 2.5rem !important;
  }
}
</style>
